/**
 * The photo control on `/account/edit`.
 *
 * Onboarding's photo step runs once and offers Skip, so this is the only
 * place a fan can add or change their photo after that. It drives the
 * shipped `useAvatarUpload` hook and implements no upload of its own.
 *
 * Four things a fan can see: their current photo or initials, a button, a
 * neutral line while a photo is waiting on a moderation result, and a
 * warning line when an upload was refused. A refused upload shows one plain
 * sentence and never a code. `useAvatarUpload` resolves a photo refused for
 * size to its own sentence and hands it over on `state.message` (ISSUE-839);
 * every other refusal arrives with `message` null and falls back to the
 * generic line below.
 *
 * A picked photo is framed first in the round crop editor, and only the
 * framed square is uploaded (ISSUE-1094).
 *
 * Removing a photo is deliberately absent. `profile-api` has no endpoint
 * that can clear `photoUrl`: `/avatar/set` is the only writer and
 * `/profile/update` dropped the field in ISSUE-125.
 */
import { Button, ImageCropEditor, InlineBanner } from "@sportsmet/ui";
import { type ReactNode, useRef, useState } from "react";

import {
  CROP_OUTPUT_MAX_EDGE_PX,
  PHOTO_CROP_ASPECT,
  PHOTO_CROP_TITLE,
} from "../imageCrop/imageCropPresets";
import { shouldOpenCropEditor } from "../imageCrop/shouldOpenCropEditor";
import { ProfileAvatar } from "../profile/ProfileAvatar";
import {
  AVATAR_UPLOAD_CONTENT_TYPES,
  MAX_AVATAR_UPLOAD_BYTES,
  useAvatarUpload,
} from "../profile/useAvatarUpload";
import {
  PHOTO_ADD_LABEL,
  PHOTO_CHANGE_LABEL,
  PHOTO_PENDING_WITH_INITIALS_MESSAGE,
  PHOTO_PENDING_WITH_PHOTO_MESSAGE,
  PHOTO_UPLOAD_FAILED_MESSAGE,
  PHOTO_UPLOADING_LABEL,
} from "./editProfileLogic";

export const EditProfilePhotoField = ({
  displayName,
  photoUrl,
}: {
  /** The profile's `displayName`, from /profile/get. Drives the initials plate. */
  displayName: string;
  /** The profile's stored `photoUrl`, from /profile/get. `null` draws initials. */
  photoUrl: string | null;
}): ReactNode => {
  const upload = useAvatarUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const resolvedPhotoUrl =
    upload.state.kind === "published" ? upload.state.photoUrl : photoUrl;

  const buttonLabel =
    resolvedPhotoUrl !== null ? PHOTO_CHANGE_LABEL : PHOTO_ADD_LABEL;

  return (
    <div className="flex flex-col gap-2">
      {upload.state.kind === "failed" ? (
        <div data-testid="edit-profile-photo-failed">
          <InlineBanner
            variant="warning"
            message={upload.state.message ?? PHOTO_UPLOAD_FAILED_MESSAGE}
          />
        </div>
      ) : upload.state.kind === "pendingReview" ? (
        <div data-testid="edit-profile-photo-pending">
          <InlineBanner
            variant="neutral"
            message={
              photoUrl !== null
                ? PHOTO_PENDING_WITH_PHOTO_MESSAGE
                : PHOTO_PENDING_WITH_INITIALS_MESSAGE
            }
          />
        </div>
      ) : null}
      <ProfileAvatar
        displayName={displayName}
        photoUrl={resolvedPhotoUrl}
        size="md"
        labelling="labelled"
        testId="edit-profile-avatar"
      />
      <Button
        variant="secondary"
        fullWidth
        className="md:w-auto md:self-start"
        loading={upload.state.kind === "uploading"}
        loadingLabel={PHOTO_UPLOADING_LABEL}
        data-testid="edit-profile-photo-upload"
        onClick={() => {
          fileInputRef.current?.click();
        }}
      >
        {buttonLabel}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="edit-profile-photo-file-input"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Clearing lets the same file fire a change again.
          event.target.value = "";
          if (file === undefined) return;
          if (
            shouldOpenCropEditor({
              file,
              contentTypes: AVATAR_UPLOAD_CONTENT_TYPES,
              maxBytes: MAX_AVATAR_UPLOAD_BYTES,
            })
          ) {
            setPendingFile(file);
            return;
          }
          upload.uploadAvatar(file);
        }}
      />
      <ImageCropEditor
        file={pendingFile}
        title={PHOTO_CROP_TITLE}
        aspect={PHOTO_CROP_ASPECT}
        shape="round"
        maxOutputEdgePx={CROP_OUTPUT_MAX_EDGE_PX}
        onCancel={() => {
          setPendingFile(null);
        }}
        onConfirm={(cropped) => {
          setPendingFile(null);
          upload.uploadAvatar(cropped);
        }}
        testId="edit-profile-photo-crop"
      />
    </div>
  );
};
